/**
 * RTK Query API service for the Notebooks feature.
 *
 * Notebooks are personal markdown documents stored in the sidebar,
 * providing a persistent scratchpad for legal operators.
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateNotebookRequest,
  LinkedMatter,
  Notebook,
  NotebookCountResponse,
  NotebookListItem,
  NotebookListResponse,
  NotebookMatterLink,
  UpdateNotebookRequest,
} from '../types/notebook-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const notebooksApi = createApi({
  reducerPath: 'notebooksApi',
  tagTypes: ['NotebookList', 'Notebook', 'NotebookMatters', 'MatterNotebooks'],
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (builder) => ({
    // ============================================
    // NOTEBOOK CRUD OPERATIONS
    // ============================================

    /**
     * List all notebooks for the authenticated user (lightweight, no content).
     */
    getNotebooks: builder.query<
      NotebookListResponse,
      { includeArchived?: boolean } | void
    >({
      query: (params) => {
        const includeArchived = params?.includeArchived ?? false;
        return `api/notebooks/${includeArchived ? '?include_archived=true' : ''}`;
      },
      providesTags: ['NotebookList'],
    }),

    /**
     * Get a single notebook with full content.
     */
    getNotebook: builder.query<Notebook, string>({
      query: (notebookId) => `api/notebooks/${notebookId}/`,
      providesTags: (result, error, notebookId) => [
        { type: 'Notebook', id: notebookId },
      ],
    }),

    /**
     * Create a new notebook.
     */
    createNotebook: builder.mutation<Notebook, CreateNotebookRequest>({
      query: (body) => ({
        url: 'api/notebooks/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NotebookList'],
      // Populate getNotebook cache with the newly created notebook
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newNotebook } = await queryFulfilled;
          // Insert the new notebook into the getNotebook cache
          dispatch(
            notebooksApi.util.upsertQueryData(
              'getNotebook',
              newNotebook.id,
              newNotebook,
            ),
          );
        } catch {
          // If the mutation fails, we don't need to do anything
        }
      },
    }),

    /**
     * Update an existing notebook.
     */
    updateNotebook: builder.mutation<
      Notebook,
      { notebookId: string; data: UpdateNotebookRequest }
    >({
      query: ({ notebookId, data }) => ({
        url: `api/notebooks/${notebookId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { notebookId }) => [
        { type: 'Notebook', id: notebookId },
        'NotebookList',
      ],
      // Optimistic update for content changes (auto-save)
      async onQueryStarted({ notebookId, data }, { dispatch, queryFulfilled }) {
        // Only optimistically update if we're updating content
        if (data.content !== undefined) {
          const patchResult = dispatch(
            notebooksApi.util.updateQueryData(
              'getNotebook',
              notebookId,
              (draft) => {
                if (data.content !== undefined) {
                  draft.content = data.content;
                }
                if (data.title !== undefined) {
                  draft.title = data.title;
                }
                if (data.position !== undefined) {
                  draft.position = data.position;
                }
                draft.modified_at = new Date().toISOString();
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        }
      },
    }),

    /**
     * Delete (archive) a notebook.
     */
    deleteNotebook: builder.mutation<void, string>({
      query: (notebookId) => ({
        url: `api/notebooks/${notebookId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NotebookList'],
    }),

    // ============================================
    // MATTER ASSOCIATION OPERATIONS
    // ============================================

    /**
     * Link a notebook to a matter.
     */
    linkNotebookToMatter: builder.mutation<
      NotebookMatterLink,
      { notebookId: string; matterId: string }
    >({
      query: ({ notebookId, matterId }) => ({
        url: `api/notebooks/${notebookId}/link-matter/`,
        method: 'POST',
        body: { matter_id: matterId },
      }),
      invalidatesTags: (result, error, { notebookId, matterId }) => [
        { type: 'NotebookMatters', id: notebookId },
        { type: 'MatterNotebooks', id: matterId },
        'NotebookList',
      ],
    }),

    /**
     * Unlink a notebook from a matter.
     */
    unlinkNotebookFromMatter: builder.mutation<
      void,
      { notebookId: string; matterId: string }
    >({
      query: ({ notebookId, matterId }) => ({
        url: `api/notebooks/${notebookId}/unlink-matter/${matterId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { notebookId, matterId }) => [
        { type: 'NotebookMatters', id: notebookId },
        { type: 'MatterNotebooks', id: matterId },
        'NotebookList',
      ],
    }),

    /**
     * Get all matters linked to a notebook.
     */
    getLinkedMatters: builder.query<LinkedMatter[], string>({
      query: (notebookId) => `api/notebooks/${notebookId}/matters/`,
      providesTags: (result, error, notebookId) => [
        { type: 'NotebookMatters', id: notebookId },
      ],
    }),

    /**
     * Get all notebooks linked to a matter (for current user).
     */
    getNotebooksForMatter: builder.query<NotebookListItem[], string>({
      query: (matterId) => `api/matters/${matterId}/notebooks/`,
      providesTags: (result, error, matterId) => [
        { type: 'MatterNotebooks', id: matterId },
      ],
    }),

    /**
     * Get count of user's notebooks linked to a matter.
     */
    getNotebookCountForMatter: builder.query<NotebookCountResponse, string>({
      query: (matterId) => `api/matters/${matterId}/notebooks/count/`,
      providesTags: (result, error, matterId) => [
        { type: 'MatterNotebooks', id: matterId },
      ],
    }),
  }),
});

export const {
  // Notebook CRUD
  useGetNotebooksQuery,
  useGetNotebookQuery,
  useCreateNotebookMutation,
  useUpdateNotebookMutation,
  useDeleteNotebookMutation,
  // Matter associations
  useLinkNotebookToMatterMutation,
  useUnlinkNotebookFromMatterMutation,
  useGetLinkedMattersQuery,
  useGetNotebooksForMatterQuery,
  useGetNotebookCountForMatterQuery,
} = notebooksApi;
